import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Rocket, CheckCircle2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import Seo from "../components/Seo";
import { SERVICES, COMPANY } from "../data/content";
import { api, formatApiError } from "../api";

const SERVICE_OPTIONS = ["Social Media Marketing", "Branding", "Influencer Marketing", "Advertising", "Content Creation", "Performance Marketing", "Other"];
const BUSINESS_TYPES = ["Startup", "SME", "Enterprise", "E-commerce Brand", "Creator / Influencer", "Personal Brand", "Local Business", "Corporate", "Events & Entertainment", "Other"];
const BUDGETS = ["Under ₹50,000", "₹50,000 – ₹1,00,000", "₹1,00,000 – ₹3,00,000", "₹3,00,000 – ₹10,00,000", "Above ₹10,00,000", "To be discussed"];

export default function StartProject() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", business_type: "", website: "",
    services: [], budget: "", message: "", hp: "",
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const s = params.get("service");
    if (s) {
      const matched = SERVICE_OPTIONS.find((o) => s.toLowerCase().includes(o.toLowerCase().split(" ")[0])) || (SERVICES.some((sv) => sv.title === s) ? "Other" : null);
      if (matched) setForm((f) => ({ ...f, services: [matched] }));
    }
  }, [params]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleService = (s) =>
    setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.services.length === 0) {
      toast.error("Please select at least one service.");
      return;
    }
    setSending(true);
    try {
      await api.post("/enquiries", { ...form, type: "project" });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full rounded-xl bg-slate-950/70 border border-slate-700 focus:border-blue-500/60 focus:outline-none px-4 py-3 text-sm text-slate-200 transition-colors";
  const labelCls = "block text-xs font-semibold text-slate-400 mb-2";

  return (
    <main className="pt-32 pb-24" data-testid="start-project-page">
      <Seo title="Start a Project" description="Tell Trendsight Media about your brand, goals and budget. Our team will review your project request and get back to you shortly." />
      <div className="max-w-4xl mx-auto px-4 sm:px-8">
        <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="font-mono-accent text-xs uppercase tracking-[0.35em] text-blue-400 mb-5 flex items-center gap-2">
          <Rocket size={14} /> Start a Project
        </motion.p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-50 leading-[1.02]" data-testid="start-headline">
          LET'S BUILD SOMETHING <span className="text-gradient">PEOPLE NOTICE.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed">
          Two minutes of detail now saves two weeks of back-and-forth later. Tell us where you are and where you want to be.
        </p>

        <div className="mt-12 rounded-2xl border border-slate-800 bg-slate-900/60 p-8 sm:p-10">
          {done ? (
            <div className="text-center py-10" data-testid="start-success">
              <CheckCircle2 size={44} className="text-emerald-400 mx-auto" />
              <h2 className="mt-5 font-display text-2xl font-bold text-slate-50">Thank you.</h2>
              <p className="mt-3 text-slate-400 max-w-md mx-auto leading-relaxed">
                Our team will review your requirement and contact you shortly.
              </p>
              <a
                href={`https://wa.me/91${COMPANY.phone}?text=${encodeURIComponent("Hi Trendsight Media, I just submitted a project request.")}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="start-whatsapp-btn"
                className="mt-7 inline-flex items-center gap-2.5 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold px-7 py-3.5 transition-all duration-300 active:scale-95"
              >
                <MessageCircle size={17} /> Chat with us on WhatsApp
              </a>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="start-form">
              <input type="text" name="hp" value={form.hp} onChange={set("hp")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className={labelCls} htmlFor="p-name">Name *</label>
                  <input id="p-name" required value={form.name} onChange={set("name")} data-testid="start-name-input" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="p-company">Company Name</label>
                  <input id="p-company" value={form.company} onChange={set("company")} data-testid="start-company-input" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="p-email">Email *</label>
                  <input id="p-email" type="email" required value={form.email} onChange={set("email")} data-testid="start-email-input" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="p-phone">Phone *</label>
                  <input id="p-phone" required value={form.phone} onChange={set("phone")} data-testid="start-phone-input" className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor="p-btype">Business Type</label>
                  <select id="p-btype" value={form.business_type} onChange={set("business_type")} data-testid="start-business-type-select" className={inputCls}>
                    <option value="">Select...</option>
                    {BUSINESS_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls} htmlFor="p-website">Website</label>
                  <input id="p-website" value={form.website} onChange={set("website")} placeholder="https://" data-testid="start-website-input" className={inputCls} />
                </div>
              </div>

              <div>
                <p className={labelCls}>Services Required *</p>
                <div className="flex flex-wrap gap-2.5" data-testid="start-services-options">
                  {SERVICE_OPTIONS.map((s) => (
                    <button
                      type="button"
                      key={s}
                      onClick={() => toggleService(s)}
                      data-testid={`start-service-${s.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`rounded-full px-4 py-2 text-[13px] font-semibold transition-all duration-300 active:scale-95 ${
                        form.services.includes(s) ? "bg-blue-600 text-white shadow-lg shadow-blue-600/25" : "border border-slate-700 text-slate-400 hover:border-blue-500/50 hover:text-blue-300"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className={labelCls} htmlFor="p-budget">Monthly Marketing Budget</label>
                <select id="p-budget" value={form.budget} onChange={set("budget")} data-testid="start-budget-select" className={inputCls}>
                  <option value="">Select...</option>
                  {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>

              <div>
                <label className={labelCls} htmlFor="p-desc">Project Description *</label>
                <textarea id="p-desc" required rows={5} value={form.message} onChange={set("message")} data-testid="start-description-input" placeholder="What are you building, who is it for, and what does success look like?" className={`${inputCls} resize-none`} />
              </div>

              <button
                type="submit"
                disabled={sending}
                data-testid="start-submit-btn"
                className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-bold tracking-wide px-8 py-4.5 py-4 transition-all duration-300 active:scale-[0.98] shadow-xl shadow-blue-600/25"
              >
                {sending ? "SUBMITTING..." : "SUBMIT PROJECT REQUEST"}
                <Rocket size={16} />
              </button>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
